import { z } from 'zod';

// Mirrors monitoring.dto.ts, monitoring.service.ts and ScreeningDecisionResultSchema.
export const statuses = ['pending', 'approved', 'hold', 'declined'] as const;
export const holdReasons = [
  'rules',
  'provider_unavailable',
  'provider_rejected',
  'standalone_aml_hit',
  'standalone_aml_review',
] as const;
const nullableText = z.string().nullable();
const amlHit = z.object({
  name: nullableText,
  match_score: nullableText,
  kinds: z.array(z.enum(['sanction', 'pep', 'adverse'])),
  datasets: z.array(z.string()),
  url: nullableText,
});
// Evidence fields are optional: rows stored before evidence validation lack them and must stay
// readable. An absent `evidence` means "not validated", never "verified".
const amlEvidence = {
  request_ref: nullableText.optional(),
  source: z.string().optional(),
  warnings: z
    .array(
      z.object({
        feature: nullableText,
        risk: nullableText,
        log_type: nullableText,
        short_description: nullableText,
        long_description: nullableText,
      }),
    )
    .optional(),
  screened: z
    .object({
      full_name: nullableText,
      entity_type: nullableText,
      date_of_birth: nullableText,
      nationality: nullableText,
    })
    .nullable()
    .optional(),
  identity: z
    .object({
      status: z.enum(['verified', 'mismatch', 'unverifiable']),
      mismatched: z.array(z.string()),
      unverified: z.array(z.string()),
    })
    .optional(),
  evidence: z
    .object({
      state: z.enum(['completed_clear', 'completed_findings', 'incomplete', 'invalid']),
      issues: z.array(z.string()),
    })
    .optional(),
};
const standalone = z.object({
  entity_type: z.literal('person'),
  raw_status: z.string().optional(),
  decision: z.enum(['approved', 'hold', 'declined']),
  hit_count: z.number(),
  hits: z.array(amlHit).optional(),
  ...amlEvidence,
});
const resultSchema = z.object({
  provider: z.enum(['didit', 'mock', 'manual']),
  raw_status: z.string(),
  score: nullableText,
  severity: nullableText,
  reason: nullableText,
  rule_runs: z.array(
    z.object({
      rule_id: z.string(),
      title: z.string(),
      mode: z.enum(['TEST', 'ACTIVE']),
      matched: z.boolean(),
      score_delta: nullableText,
      severity: nullableText,
    }),
  ),
  counterparty_aml: z
    .object({
      raw_status: z.string(),
      decision: z.enum(['approved', 'hold', 'declined']),
      score: nullableText,
      hit_count: z.number(),
      hits: z.array(amlHit),
      ...amlEvidence,
    })
    .nullable(),
  transaction_evidence: z
    .object({
      counterparty_aml: z.object({
        availability: z.enum(['unavailable', 'unverified_source']),
        source: nullableText,
      }),
      provider_results: z.array(
        z.object({
          result_type: nullableText,
          provider: nullableText,
          status: nullableText,
          severity: nullableText,
        }),
      ),
      parties: z.array(
        z.object({ role: nullableText, entity_type: nullableText, vendor_data: nullableText }),
      ),
    })
    .optional(),
  standalone_aml: standalone.nullable(),
  provider_after_manual: z
    .object({
      raw_status: z.string(),
      decision: z.enum(['approved', 'hold', 'declined']),
      received_at: z.string(),
      standalone_aml: standalone.nullable().optional(),
    })
    .nullable(),
  custom_properties_sent: z.record(
    z.union([z.string(), z.boolean(), z.array(z.string()), z.null()]),
  ),
  provider_activities: z
    .array(
      z.object({
        id: z.string(),
        occurredAt: z.string(),
        actorName: nullableText,
        actorEmail: nullableText,
        previousStatus: z.string(),
        newStatus: z.string(),
        reasonCode: nullableText,
        reasonLabel: nullableText,
      }),
    )
    .optional(),
});
export const summarySchema = z.object({
  screening_id: z.string(),
  tenant_cod: z.string(),
  transaction_id: z.string(),
  transaction_type: z.enum(['outbound', 'incoming']),
  client_id: z.string(),
  status: z.enum(statuses),
  hold_reason: z.enum(holdReasons).nullable(),
  provider: nullableText,
  provider_ref: nullableText,
  decision_source: nullableText,
  enforcement_mode: nullableText,
  requested_at: z.string(),
  decided_at: nullableText,
  applied_at: nullableText,
  attempts: z.number(),
  next_attempt_at: nullableText,
  last_error: nullableText,
  last_synced_at: nullableText,
  override: z.object({
    status: z.string(),
    by: nullableText,
    reason: nullableText,
    at: nullableText,
  }),
});
export const detailSchema = summarySchema.extend({
  result: resultSchema,
  hits: z.array(
    z.object({
      hit_id: z.string(),
      type: z.string(),
      source: z.string(),
      matched_name: nullableText,
      match_score: nullableText,
      details: z.unknown(),
    }),
  ),
  events: z.array(
    z.object({
      event_id: z.string(),
      label: z.string(),
      actor: z.string(),
      occurred_at: z.string(),
      payload: z.record(z.unknown()),
      delivery_status: z.enum(['pending', 'delivered']),
      delivery_attempts: z.number(),
      delivery_next_attempt_at: nullableText,
      delivery_last_error: nullableText,
      delivered_at: nullableText,
    }),
  ),
});
// Mirrors screening-context.service.ts: values copied from CBS-owned records, null when absent.
export const contextSchema = z.object({
  screening_id: z.string(),
  transaction_id: z.string(),
  direction: z.enum(['outbound', 'incoming']),
  record: z
    .object({
      source: z.enum(['cbs_payment', 'cbs_incoming_payment']),
      reference: nullableText,
      amount: nullableText,
      currency: nullableText,
      status: nullableText,
    })
    .nullable(),
  client: z.object({ id: z.string(), name: nullableText }),
  counterparty: z.object({ role: z.enum(['beneficiary', 'sender']), name: nullableText }),
});
export const transactionLookupSchema = z.object({ screening: summarySchema.nullable() });
export const listSchema = z.object({
  items: z.array(summarySchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});
export type ScreeningSummary = z.infer<typeof summarySchema>;
export type ScreeningDetail = z.infer<typeof detailSchema>;
export type ScreeningContext = z.infer<typeof contextSchema>;
export type MonitoringFilters = {
  status?: (typeof statuses)[number];
  type?: 'outbound' | 'incoming';
  hold_reason?: (typeof holdReasons)[number];
  from?: string;
  to?: string;
  page: number;
  limit: number;
};
export type DecisionBody = { decision: 'approve' | 'decline'; reason: string };
export function canDecide(row: ScreeningSummary) {
  return (
    row.status === 'hold' &&
    row.applied_at === null &&
    row.hold_reason !== null &&
    row.hold_reason !== 'rules'
  );
}
export function canResubmit(row: ScreeningSummary) {
  return canDecide(row) && row.hold_reason === 'provider_rejected';
}
export function validReason(reason: string) {
  return reason.trim().length >= 10 && reason.trim().length <= 1000;
}
export function label(value: string | null | undefined) {
  return value
    ? value
        .replaceAll('_', ' ')
        .replace(/\baml\b/gi, 'AML')
        .replace(/^./, (c) => c.toUpperCase())
    : 'Not available';
}
